import { ArrowLeft, ExternalLink, FileText, LoaderCircle, Send, User } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { sendChatMessage } from "../lib/api";
import type { ChatSource, HubSearchResult } from "../lib/types";

type McsChatWorkspaceProps = {
  sources: HubSearchResult[];
  initialQuery: string;
  onBack: () => void;
  onOpenSource: (source: HubSearchResult) => void;
};

type TranscriptMessage = {
  id: string;
  role: "user" | "assistant";
  content: string;
  citations?: ChatSource[];
};

export function McsChatWorkspace({
  sources,
  initialQuery,
  onBack,
  onOpenSource,
}: McsChatWorkspaceProps) {
  const [threadId] = useState(createId);
  const [messages, setMessages] = useState<TranscriptMessage[]>([]);
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const initialSent = useRef(false);
  const transcriptEnd = useRef<HTMLDivElement | null>(null);

  const sourceById = useMemo(
    () => new Map(sources.map((source) => [source.id, source])),
    [sources],
  );

  useEffect(() => {
    if (initialSent.current || !initialQuery.trim()) return;
    initialSent.current = true;
    void submit(initialQuery, []);
  }, []);

  useEffect(() => {
    transcriptEnd.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, loading]);

  async function submit(value: string, history = messages) {
    const message = value.trim();
    if (!message || loading) return;
    const userMessage: TranscriptMessage = { id: createId(), role: "user", content: message };
    setMessages([...history, userMessage]);
    setDraft("");
    setError("");
    setLoading(true);
    try {
      const response = await sendChatMessage({
        threadId,
        message,
        history: history.slice(-20).map(({ role, content }) => ({ role, content })),
        sourceDocumentIds: sources.map((source) => source.id),
      });
      setMessages((current) => [...current, {
        id: response.responseId || createId(),
        role: "assistant",
        content: response.answer,
        citations: response.sources,
      }]);
    } catch (chatError) {
      setError(chatError instanceof Error ? chatError.message : "Lwazi could not answer right now.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="mcs-chat-workspace">
      <header className="mcs-chat-workspace__header">
        <button className="secondary-action" type="button" onClick={onBack}><ArrowLeft size={17} /> Back to results</button>
        <div className="mcs-chat-workspace__heading"><img src="/lwazi/headset.png" alt="" aria-hidden="true" /><div><p className="section-kicker">Lwazi · MCS document chat</p><h1>Ask across {sources.length} selected source{sources.length === 1 ? "" : "s"}</h1><p>Answers are grounded in the public source briefs. Follow the citations to check the original guidance.</p></div></div>
      </header>

      <section className="chat-source-strip" aria-label="Selected chat sources">
        {sources.map((source, index) => (
          <button type="button" className="chat-source-chip" key={source.id} onClick={() => onOpenSource(source)}>
            <FileText size={16} /><span><strong>Source {index + 1}</strong>{source.title}</span><ExternalLink size={14} />
          </button>
        ))}
      </section>

      <section className="chat-transcript" aria-live="polite">
        {messages.length === 0 ? (
          <div className="chat-welcome">
            <span><img src="/lwazi/headset.png" alt="" aria-hidden="true" /></span>
            <div><h2>Hi, I’m Lwazi. What would you like to understand?</h2><p>Ask for a summary, comparison, requirement, exception, practical implication, or explanation from the selected MCS sources.</p></div>
          </div>
        ) : null}
        {messages.map((message) => (
          <article className={`chat-message chat-message--${message.role}`} key={message.id}>
            <div className="chat-message__avatar">{message.role === "assistant" ? <img src="/lwazi/reading.png" alt="" aria-hidden="true" /> : <User size={18} />}</div>
            <div className="chat-message__body">
              <strong>{message.role === "assistant" ? "Lwazi" : "You"}</strong>
              {message.role === "assistant" ? (
                <div className="chat-message__markdown">
                  <ReactMarkdown remarkPlugins={[remarkGfm]} skipHtml>{linkSourceLabels(message.content, message.citations ?? [])}</ReactMarkdown>
                </div>
              ) : <p>{message.content}</p>}
              {message.role === "assistant" && message.citations?.length ? (
                <div className="chat-citations" aria-label="Sources cited in this answer">
                  {message.citations.map((citation, index) => {
                    const result = sourceById.get(citation.documentId);
                    return (
                      <button type="button" key={`${message.id}-${citation.documentId}`} onClick={() => result && onOpenSource(result)} disabled={!result}>
                        <span>{index + 1}</span><strong>{citation.title}</strong><ExternalLink size={13} />
                      </button>
                    );
                  })}
                </div>
              ) : null}
            </div>
          </article>
        ))}
        {loading ? (
          <article className="chat-message chat-message--assistant chat-message--loading">
            <div className="chat-message__avatar"><img src="/lwazi/thinking.png" alt="" aria-hidden="true" /></div>
            <div className="chat-message__body"><strong>Lwazi</strong><p>Reading the selected source briefs…</p></div>
          </article>
        ) : null}
        <div ref={transcriptEnd} />
      </section>

      {error ? <p className="chat-error" role="alert">{error}</p> : null}

      <form className="chat-composer" onSubmit={(event) => { event.preventDefault(); void submit(draft); }}>
        <label><span className="sr-only">Message</span><textarea rows={3} maxLength={8_000} value={draft} placeholder="Ask Lwazi a follow-up about the selected MCS sources…" onChange={(event) => setDraft(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter" && !event.shiftKey) { event.preventDefault(); void submit(draft); } }} /></label>
        <button className="primary-action" type="submit" disabled={loading || !draft.trim()}>{loading ? <LoaderCircle className="spin" size={18} /> : <Send size={18} />} Send</button>
      </form>
    </main>
  );
}

function linkSourceLabels(answer: string, sources: ChatSource[]) {
  return answer.replace(/\[Source\s+(\d+)\](?!\()/gi, (label, numberText: string) => {
    const source = sources[Number(numberText) - 1];
    if (!source?.url || !isSafeUrl(source.url)) return label;
    return `[${label.slice(1, -1)}](${source.url})`;
  });
}

function isSafeUrl(value: string) {
  try {
    const url = new URL(value, window.location.origin);
    return url.protocol === "https:" || url.protocol === "http:";
  } catch {
    return false;
  }
}

function createId() {
  return globalThis.crypto?.randomUUID?.() ?? `mcs-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}
